import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref, watch, onUnmounted, Ref } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import { TranslateResult } from 'vue-i18n';

import SearchBar from '@/features/search/components/SearchBar.vue';
import PackageRow from '@/components/modals/package-selection/PackageRow.vue';
import PackageExtrasModal from '@/components/modals/package-selection/PackageExtrasModal.vue';
import V1EmptyState from '@/components/experiments/ProviderBuyboxExp/V1EmptyState.vue';
import BuyboxResetButton from '@/components/experiments/ProviderBuyboxExp/BuyboxResetButton.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import {
	SelectStandalonePackageDocument,
	SelectStandalonePackageMutation,
	SelectStandalonePackageMutationVariables,
} from '@/components/modals/package-selection/graphql/mutations/SelectPackage.mutation';

import {
	GetPackagesDocument,
	GetPackagesQuery,
	GetPackagesQueryVariables,
} from '@/components/modals/package-selection/graphql/queries/GetPackages.query';
import { useI18n } from '@/helpers/composables/useI18n';
import { useMutation, useQuery } from '@/helpers/composables/useApollo';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { arraysAreEquals } from '@/helpers/object-helper';
import { toastError } from '@/helpers/vfm/toast-helper';
import { SnowplowPackageContext } from '@/helpers/tracking/providers';
import { MonetizationType, Package } from '@/@types/graphql-types';
import { PackagesTracker } from '@/components/modals/package-selection/enums';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';

type PackageSelectionModalTabType = 'subscription' | 'free' | 'buy' | 'cinema';

interface PackageSelectionModalTab {
	text: TranslateResult;
	monetizationTypes: MonetizationType[];
	type: PackageSelectionModalTabType;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProviderFilterMenu',
  props: {
    buyboxType: null
  },
  emits: ['dismiss'],
  setup(__props: any, { emit }) {

const props = __props as {
	buyboxType: 'grid' | 'list';
};





const { tc } = useI18n();
const { country } = storeToRefs(useLanguageStore());
const userStore = useUserStore();
const { saveSettings } = userStore;
const { settingsProvider } = storeToRefs(userStore);
const { trackEvent } = useExperiment('EXP_PROVIDER_BUYBOX');

const tabs: PackageSelectionModalTab[] = [
	{
		text: tc('WEBAPP_FLATRATE'),
		monetizationTypes: [MonetizationType.Flatrate, MonetizationType.FlatrateAndBuy],
		type: 'subscription',
	},
	{
		text: tc('WEBAPP_FREE'),
		monetizationTypes: [MonetizationType.Free, MonetizationType.Ads],
		type: 'free',
	},
	{
		text: `${tc('WEBAPP_BUY')}/${tc('WEBAPP_RENT')}`,
		monetizationTypes: [MonetizationType.Rent, MonetizationType.Buy],
		type: 'buy',
	},
	{
		text: `${tc('WEBAPP_CINEMA')}`,
		monetizationTypes: [MonetizationType.Cinema],
		type: 'cinema',
	},
];

// injected ref from parent buybox to sync selected providers
const buyboxProviderFilter = inject<Ref<string[] | null> | null>('buyboxProviderFilter', null);
const closeBuyboxProviderMenu = inject<(() => void) | null>('closeBuyboxProviderMenu', null);
// Direct setter from GridBuyBox — bypasses Vue 2.7 computed chain for immediate reactivity
const setActiveProviderFilter = inject<((val: string[] | null) => void) | null>('setActiveProviderFilter', null);

const isPackagesFetched = ref(false);
const initialSelectedPackageIds = ref<string[]>([]);
const search = ref('');
const activeTab = ref<PackageSelectionModalTab>(tabs[0]);
// Local checkmark state — independent of account-level p.selected
const localSelected = ref<string[]>([]);

const { result, onResult, onError } = useQuery<GetPackagesQuery, GetPackagesQueryVariables>(
	GetPackagesDocument,
	() => ({ country: country.value }),
	{ prefetch: false }
);

onResult(() => {
	isPackagesFetched.value = true;
});

onError(() => showErrorToast());

const packages = computed(() => (result.value?.packages || []).filter(p => p.isVisibleFilterBar) as Package[]);

const { mutate, onError: onMutationError } = useMutation<
	SelectStandalonePackageMutation,
	SelectStandalonePackageMutationVariables
>(SelectStandalonePackageDocument);

onMutationError(() => showErrorToast());

const selectedPackages = computed(() =>
	packages.value
		.filter(p => !p.addonParent && p.selected)
		.reduce((acc: Package[], p) => {
			const selectedAddons = p.addons.filter(a => a.selected);
			return [...acc, p, ...selectedAddons];
		}, [])
);

const packagesToDisplay = computed(() => {
	const withLocalSelected = packages.value.map(p => ({ ...p, selected: localSelected.value.includes(p.shortName) }));
	if (search.value) {
		return withLocalSelected.filter(p => p.clearName.toUpperCase().includes(search.value.toUpperCase()));
	}
	return [...withLocalSelected].sort((a, b) => Number(b.selected) - Number(a.selected));
});

function trackSearchBarFocus() {
	TrackingHelper.trackEvent('user_account', {
		action: PackagesTracker.SEARCH_ACTIVATE,
		property: PackagesTracker.SETTINGS,
		label: String(activeTab.value.text),
	});

	trackEvent({
		action: 'click',
		label: 'provider_search_focus',
		value: props.buyboxType === 'grid' ? 0 : 1,
	});
}

function getSelectedPackageContexts(selectedPackages: Package[]) {
	return selectedPackages.reduce((packageContexts: SnowplowPackageContext[], packageItem: Package) => {
		let packageItemContexts = [TrackingHelper.getPackageContext(packageItem.packageId, packageItem.technicalName)];
		const selectedAddons = packageItem.addons?.filter(a => a.selected) || [];
		const selectedBundles = packageItem.bundles?.filter(a => a.selected) || [];

		if (selectedAddons.length) {
			const selectedAddonContexts = selectedAddons.map(a =>
				TrackingHelper.getPackageContext(
					a.packageId,
					a.technicalName,
					'package',
					packageItem.packageId,
					packageItem.technicalName
				)
			);
			packageItemContexts = packageItemContexts.concat(selectedAddonContexts);
		}

		if (selectedBundles.length) {
			const selectedBundleContexts = selectedBundles.map(b =>
				TrackingHelper.getPackageContext(
					packageItem.packageId,
					packageItem.technicalName,
					'bundle',
					b.bundleId,
					b.technicalName
				)
			);
			packageItemContexts = packageItemContexts.concat(selectedBundleContexts);
		}

		return packageContexts.concat(packageItemContexts);
	}, []);
}

async function togglePackage(packageItem: Package, state?: boolean) {
	const itemState = state ?? !packageItem.selected;

	await mutate({
		state: itemState,
		country: country.value,
		packagesShortName: packageItem.shortName,
		cascadeDeselectBundles: true,
	});

	TrackingHelper.trackEvent(
		'user_account',
		{
			action: PackagesTracker.TOGGLED,
			property: PackagesTracker.SETTINGS,
			label: String(packageItem.packageId),
			value: Number(itemState),
		},
		[TrackingHelper.getPackageContext(packageItem.packageId, packageItem.technicalName)]
	);
}

function handleDone() {
	const parentShortNames = localSelected.value;
	const selectedAddonShortNames = packages.value
		.filter(p => parentShortNames.includes(p.shortName))
		.flatMap(p => p.addons.filter(a => a.selected).map(a => a.shortName));
	const providers = [...new Set([...parentShortNames, ...selectedAddonShortNames])];
	setActiveProviderFilter?.(providers);
	if (buyboxProviderFilter) buyboxProviderFilter.value = providers;
	closeBuyboxProviderMenu?.();

	trackEvent({
		action: 'click',
		label: 'provider_filter_close',
		property: selectedPackages.value.map(poo => poo.technicalName).join(', '),
		value: props.buyboxType === 'grid' ? 0 : 1,
	});
}

function onReset() {
	setActiveProviderFilter?.(null);
	if (buyboxProviderFilter) buyboxProviderFilter.value = null;
	closeBuyboxProviderMenu?.();

	trackEvent({
		action: 'click',
		label: 'reset',
		value: props.buyboxType === 'grid' ? 0 : 1,
	});

	trackEvent({
		action: 'click',
		label: 'provider_filter_close',
		value: props.buyboxType === 'grid' ? 0 : 1,
	});
}

async function packageClickHandler(packageItem: Package) {
	const { addons = [], bundles = [] } = packageItem;
	// packageItem.selected is the local-override value from packagesToDisplay
	const isSelected = packageItem.selected;

	if (addons.length > 0 || bundles.length > 0) {
		if (!isSelected) {
			localSelected.value = [...localSelected.value, packageItem.shortName];
			await togglePackage(packageItem);
		}

		TrackingHelper.trackEvent(
			'user_account',
			{
				action: PackagesTracker.PACKAGE_DETAILS,
				property: PackagesTracker.SETTINGS,
				label: String(packageItem.packageId),
				value: 0,
			},
			[TrackingHelper.getPackageContext(packageItem.packageId, packageItem.technicalName)]
		);

		await ModalHelper.openModal(PackageExtrasModal, {
			packageId: packageItem.id,
			onPackageToggle: togglePackage,
		});

		const pkg = packages.value.find(p => p.shortName === packageItem.shortName);
		if (pkg) {
			localSelected.value = pkg.selected
				? [...new Set([...localSelected.value, pkg.shortName])]
				: localSelected.value.filter(s => s !== pkg.shortName);
		}
		return;
	}

	localSelected.value = isSelected
		? localSelected.value.filter(s => s !== packageItem.shortName)
		: [...localSelected.value, packageItem.shortName];
	await togglePackage(packageItem);

	trackEvent({
		action: 'click',
		label: 'provider_toggle',
		property: packageItem.technicalName,
		value: props.buyboxType === 'grid' ? 0 : 1,
	});
}

async function saveSelectedPackages() {
	if (packages.value.length === 0) {
		return;
	}

	const payload = selectedPackages.value.map(({ shortName }) => shortName);
	if (payload.length === 0) return;

	saveSettings({ providers: payload });
}

async function onModalClose() {
	await saveSelectedPackages();

	const selectedPackageIds = selectedPackages.value.map(p => p.id);
	const isPackagesUpdated = !arraysAreEquals(initialSelectedPackageIds.value, selectedPackageIds);
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'modal_closed',
			property: PackagesTracker.SETTINGS,
			label: 'PackageSelectionModal',
			value: isPackagesUpdated ? 1 : 0,
		},
		getSelectedPackageContexts(selectedPackages.value)
	);

	emit('dismiss');
}

let searchDebounceTimer: number | null = null;
watch(search, term => {
	if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
	if (!term) return;
	searchDebounceTimer = setTimeout(() => {
		trackEvent({
			action: 'click',
			label: 'provider_search_focus',
			property: term,
			value: props.buyboxType === 'grid' ? 0 : 1,
		});
	}, 1500);
});

onUnmounted(() => {
	onModalClose();
	if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});

function showErrorToast() {
	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
}

watch(isPackagesFetched, () => {
	initialSelectedPackageIds.value = selectedPackages.value.map(p => p.id);
	localSelected.value = settingsProvider.value ?? [];
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'modal_opened',
			property: PackagesTracker.SETTINGS,
			label: 'PackageSelectionModal',
		},
		getSelectedPackageContexts(selectedPackages.value)
	);
});

return { __sfc: true,props, emit, tc, country, userStore, saveSettings, settingsProvider, trackEvent, tabs, buyboxProviderFilter, closeBuyboxProviderMenu, setActiveProviderFilter, isPackagesFetched, initialSelectedPackageIds, search, activeTab, localSelected, result, onResult, onError, packages, mutate, onMutationError, selectedPackages, packagesToDisplay, trackSearchBarFocus, getSelectedPackageContexts, togglePackage, handleDone, onReset, packageClickHandler, saveSelectedPackages, onModalClose, searchDebounceTimer, showErrorToast, SearchBar, PackageRow, V1EmptyState, BuyboxResetButton, BasicButton }
}

})